import type { MediaFormat, Product, ProductVariant } from '@prisma/client'
import type { SerializeFrom } from '@remix-run/server-runtime'

import type { ProductFull } from '~/models/product.server'
import type { AmendableAlterationsWithAmendableTraits } from '~/schemas/product_alteration'
import type { AmendableMarkers } from '~/schemas/product_marker'
import type { AmendableTrait } from '~/schemas/product_trait'
import type { AmendableVariant } from '~/schemas/product_variant'

const DEFAULT_PRODUCT_MEDIA_FORMAT: MediaFormat = 'square'

export function makeVariantName(
  traits: AmendableTrait[] = [],
  fallback: string = ''
) {
  const variantName = traits.map((trait) => trait.name).join(' ')

  return variantName || fallback
}

export function makeVariantFullName(
  traits: AmendableTrait[] = [],
  productName: Product['name']
) {
  const variantName = makeVariantName(traits)

  return variantName ? `${productName} ${variantName}` : productName
}

export function makeVariantKey(markers: AmendableMarkers = []) {
  const variantKey = markers
    .sort(
      (m1, m2) =>
        (m2.trait.alteration.creationOrder || 0) -
        (m1.trait.alteration.creationOrder || 0)
    )
    .map(
      (marker) =>
        `${marker.trait.alteration.creationOrder}_${marker.trait.creationOrder}`
    )
    .join('-')

  return variantKey || '-'
}

const DEFAULT_VARIANT_ATTRIBUTES = {
  stock: 0,
  price: 0,
  isVisible: true,
}

export type AmendableVariantType = AmendableVariant & {
  id?: string
  key?: string
}

export function makeVariants(
  productName: Product['name'],
  alterations: AmendableAlterationsWithAmendableTraits,
  oldVariants?: AmendableVariantType[]
): AmendableVariantType[] {
  const markersMatrix = generateVariantMarkersCombinations(alterations)

  if (markersMatrix.length === 1 && markersMatrix[0]!.length === 0) {
    // special case: single variant
    const key = makeVariantKey()
    const oldVariant = oldVariants?.find(
      (v) => makeVariantKey(v.markers) == key
    )
    return [
      {
        key: makeVariantKey(),
        name: makeVariantName([], productName),
        ...DEFAULT_VARIANT_ATTRIBUTES,
        ...oldVariant,
        markers: [],
      },
    ]
  }

  const allVariants = markersMatrix.map((markers) => {
    const key = makeVariantKey(markers)

    const name = makeVariantName(
      markers.map((m) => m.trait),
      productName
    )
    const isDefault = markers.every((m) => m.trait.isDefault === true)
    const oldVariant = oldVariants?.find(
      (v) => makeVariantKey(v.markers) == key
    )

    return {
      key: key,
      name: name,
      ...DEFAULT_VARIANT_ATTRIBUTES,
      ...oldVariant,
      isDefault: isDefault,
      markers,
    }
  })

  return allVariants
}

function generateVariantMarkersCombinations(
  alterations: AmendableAlterationsWithAmendableTraits
) {
  const result: AmendableMarkers[] = []

  function recursive(acc: AmendableMarkers, alterationIdx = 0) {
    const alteration = alterations[alterationIdx]
    if (!alteration) {
      // no more alterations at this level; add to result and stop
      result.push(acc)
      return
    }

    for (const trait of alteration.traits) {
      // clone acc and add trait
      const newTrait =
        'id' in trait
          ? {
              id: trait.id,
              name: trait.name,
              isDefault: trait.isDefault,
              creationOrder: trait.creationOrder,
            }
          : {
              name: trait.name,
              isDefault: trait.isDefault,
              creationOrder: trait.creationOrder,
            }
      const newAlteration =
        'id' in alteration
          ? {
              id: alteration.id,
              name: alteration.name,
              creationOrder: alteration.creationOrder,
            }
          : { name: alteration.name, creationOrder: alteration.creationOrder }
      const newMarker = {
        trait: {
          ...newTrait,
          alteration: newAlteration,
        },
      }

      const newAcc = [...acc, newMarker]

      // recurse to next level
      recursive(newAcc, alterationIdx + 1)
    }
  }

  // start the recursive function with an empty array
  recursive([])

  return result
}

export function getProductDisplayPrice(
  product: SerializeFrom<{
    variants?: (AmendableVariantType & ProductVariant)[]
  }>,
  discount: number | null
) {
  const defaultVariant = product.variants?.find((variant) => {
    return (
      variant.isVisible &&
      !variant.archivedAt &&
      variant.markers.every((m) => m.trait.isDefault)
    )
  })

  const firstVisibleVariant = product.variants?.find((variant) => {
    return variant.isVisible && !variant.archivedAt
  })

  const price =
    (defaultVariant ? defaultVariant.price : firstVisibleVariant?.price) || 0

  return discount ? price - price * (discount / 100) : price
}

export function getProductMediaFormat(
  mediaFormat?: MediaFormat | null
): MediaFormat {
  return mediaFormat || DEFAULT_PRODUCT_MEDIA_FORMAT
}

export function getCreationOrder(): number {
  // Calculates the current time in milliseconds, converts it to seconds
  // and the module ensures that the generated number doesn't exceed 999,999,999
  // and wraps around if it does
  return Math.round((new Date().getTime() / 1000) % 1000000000)
}

export function parseShopProduct(
  product: SerializeFrom<ProductFull>
): SerializeFrom<ProductFull> {
  const now = new Date().toISOString()

  let status: ProductFull['status'] = 'live'

  if (product.isPreview && !product.isPresale) {
    status = 'preview'
  } else if (
    product.isPresale &&
    !product.isPreview &&
    product.publishedAt &&
    product.publishedAt >= now
  ) {
    status = 'presale'
  }

  return {
    ...product,
    status,
  }
}

export function getProductMediaAttachment(product: SerializeFrom<ProductFull>) {
  // Find the default visible variant first
  const defaultVariant = (product.variants || []).find(
    (v) =>
      v.archivedAt == null &&
      v.isVisible &&
      v.markers.every((m) => m.trait.isDefault)
  )

  // If no default visible variant, find any visible variant
  const visibleVariant =
    defaultVariant ||
    (product.variants || []).find((v) => v.archivedAt == null && v.isVisible)

  const variantAtts = (product.attachments || [])
    .filter((a) => a.archivedAt == null && a.targetId == visibleVariant?.id)
    .sort((a, b) => Number(a.disposition) - Number(b.disposition))

  return (
    variantAtts.length > 0
      ? variantAtts[0]
      : (product.attachments || [])
          .filter((a) => a.archivedAt == null && a.targetType == 'generic')
          .sort((a, b) => Number(a.disposition) - Number(b.disposition))?.[0]
  )?.media
}
